/**
 * @fileoverview Logger condicional para ambiente de desenvolvimento.
 *
 * Em produção, apenas erros críticos são logados.
 * Em desenvolvimento, todos os logs são exibidos com formatação colorida.
 *
 * @example
 * import { logger } from '@/utils/logger';
 *
 * logger.log('Mensagem informativa');
 * logger.debug('Debug detalhado', { data });
 * logger.warn('Aviso importante');
 * logger.error('Erro crítico', error);
 * logger.security('Evento de segurança', { userId, action });
 */

// Vue CLI usa process.env, não import.meta.env (que é do Vite)
const isDev = process.env.NODE_ENV === "development";
const isTest = process.env.NODE_ENV === "test";

// Prefixos coloridos para desenvolvimento
const PREFIX = {
  log: "📋",
  debug: "🔍",
  warn: "⚠️",
  error: "❌",
  security: "🔐",
  router: "🚦",
  api: "🌐",
  auth: "🔑",
};

/**
 * Formata argumentos para log, removendo dados sensíveis em produção
 * @param {Array} args - Argumentos do log
 * @returns {Array} Argumentos sanitizados
 */
function sanitizeArgs(args) {
  if (isDev) return args;

  return args.map((arg) => {
    if (typeof arg === "object" && arg !== null) {
      // Remove campos sensíveis em produção
      const sensitiveKeys = [
        "token",
        "password",
        "senha",
        "refresh_token",
        "accessToken",
      ];
      const sanitized = { ...arg };
      sensitiveKeys.forEach((key) => {
        if (key in sanitized) {
          sanitized[key] = "[REDACTED]";
        }
      });
      return sanitized;
    }
    return arg;
  });
}

/**
 * Logger principal com métodos para diferentes níveis
 */
export const logger = {
  /**
   * Log informativo - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  log: (...args) => {
    if (isDev && !isTest) {
      console.log(PREFIX.log, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de debug detalhado - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  debug: (...args) => {
    if (isDev && !isTest) {
      console.debug(PREFIX.debug, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de aviso - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  warn: (...args) => {
    if (isDev && !isTest) {
      console.warn(PREFIX.warn, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de erro - SEMPRE logado (inclusive em produção)
   * Erros são importantes para debugging em qualquer ambiente
   * @param {...any} args - Argumentos para log
   */
  error: (...args) => {
    if (!isTest) {
      console.error(PREFIX.error, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de eventos de segurança - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  security: (...args) => {
    if (isDev && !isTest) {
      console.log(PREFIX.security, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de navegação/router - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  router: (...args) => {
    if (isDev && !isTest) {
      console.log(PREFIX.router, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de chamadas API - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  api: (...args) => {
    if (isDev && !isTest) {
      console.log(PREFIX.api, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de autenticação - apenas em desenvolvimento
   * @param {...any} args - Argumentos para log
   */
  auth: (...args) => {
    if (isDev && !isTest) {
      console.log(PREFIX.auth, ...sanitizeArgs(args));
    }
  },

  /**
   * Log de grupo (para agrupar logs relacionados)
   * @param {string} label - Label do grupo
   * @param {Function} fn - Função com logs a agrupar
   */
  group: (label, fn) => {
    if (isDev && !isTest) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  },

  /**
   * Log de tabela - útil para arrays/objetos
   * @param {Array|Object} data - Dados para exibir em tabela
   */
  table: (data) => {
    if (isDev && !isTest) {
      console.table(data);
    }
  },
};

// Exportação default para compatibilidade
export default logger;
